import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StackedCalculatorCard = ({ 
  title, 
  description, 
  icon, 
  features, 
  isActive, 
  onClick,
  completionRate = 0,
  estimatedTime,
  priority = 'medium'
}) => {
  const priorityColors = {
    high: 'gradient-executive',
    medium: 'gradient-accent', 
    low: 'bg-success'
  };

  const priorityBorders = {
    high: 'border-error/30',
    medium: 'border-accent/30',
    low: 'border-success/30'
  };

  const priorityIndicators = {
    high: 'bg-error',
    medium: 'bg-accent',
    low: 'bg-success'
  };

  return (
    <div 
      className={`relative rounded-2xl p-8 cursor-pointer transition-all duration-500 border-2 backdrop-blur-sm ${
        isActive 
          ? `${priorityBorders[priority]} shadow-executive-hover bg-card` 
          : `border-border hover:border-border/60 bg-card hover:shadow-executive`
      }`}
      onClick={onClick}
    >
      {/* Priority indicator */}
      <div className={`absolute top-4 right-4 w-3 h-3 rounded-full ${priorityIndicators[priority]}`} />
      
      {/* Header section */}
      <div className="flex items-start justify-between mb-6">
        <div className={`w-16 h-16 rounded-xl flex items-center justify-center shadow-executive ${
          isActive ? priorityColors[priority] : 'bg-muted'
        }`}>
          <Icon 
            name={icon} 
            size={28} 
            color={isActive ? 'var(--color-accent-foreground)' : 'var(--color-text-primary)'} 
          />
        </div>
        
        {completionRate > 0 && (
          <div className="flex flex-col items-end space-y-1">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-text-secondary">{completionRate}%</span>
              <Icon name="TrendingUp" size={16} color="var(--color-accent)" />
            </div>
            <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-accent transition-all duration-1000 rounded-full"
                style={{ width: `${completionRate}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Content section */}
      <div className="mb-6">
        <h3 className="font-headline font-bold text-xl text-text-primary mb-3">
          {title}
        </h3>
        
        <p className="text-text-secondary leading-relaxed mb-4">
          {description}
        </p>
      </div>

      {/* Features list */}
      <div className="space-y-3 mb-6">
        {features?.map((feature, index) => (
          <div key={index} className="flex items-center space-x-3">
            <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center">
              <Icon name="Check" size={12} color="var(--color-accent)" />
            </div>
            <span className="text-sm text-text-secondary font-medium">{feature}</span>
          </div>
        ))}
      </div>

      {/* Footer section */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-2 text-text-secondary">
          <Icon name="Clock" size={18} color="var(--color-text-secondary)" />
          <span className="text-sm font-medium">{estimatedTime}</span>
        </div>
        
        <Button
          variant={isActive ? "default" : "outline"}
          size="lg"
          className={`transition-all duration-300 ${
            isActive 
              ? `${priorityColors[priority]} text-accent-foreground border-0 hover:opacity-90` 
              : "hover:border-primary hover:text-primary"
          }`}
          iconName={isActive ? "ArrowRight" : "Calculator"}
          iconPosition="right"
        >
          {isActive ? 'Continue' : 'Start Calculator'}
        </Button>
      </div>

      {/* Subtle animation indicator */}
      {isActive && (
        <div className="absolute inset-0 rounded-2xl bg-accent/5 pointer-events-none" />
      )}
    </div>
  );
};

export default StackedCalculatorCard;